/* eslint-disable camelcase */
/* eslint-disable max-lines-per-function */
import { useQuery } from "@apollo/client";
import { Link, Match, RouteComponentProps, Router, navigate } from "@reach/router";
import css from "classnames";
import { identity } from "lodash";
import React from "react";
import { Grid } from "@material-ui/core";
import { Viewer } from "components/auth/types";
import { ServerError } from "components/graphql-views";
import {
  Feature,
  QueryInterestProfile,
  QueryInterestProfileVariables,
} from "types/gql-types";
import { isPropertyFeatureEnabled } from "pages/dashboard/interests";
import { useSidebarFilters } from "pages/dashboard/interests/sidebar-filters";
import Activity from "../../interests-activities/activity-page";
import Documents from "../../interests-documents/components/index";
import InterestUpdatePage from "../../interests-update/interest-update-page";
import { QUERY_INTEREST_PROFILE } from "../query";
import Actions from "./actions";
import Details from "./details";
import Download from "./download";
import styles from "./index.module.css";
import Navigation from "./navigation";

interface Props {
  interestID?: string;
  viewer: Viewer;
}

export const ApplicantProfileView: React.FC<RouteComponentProps & Props> = (props) => {
  const { pageState } = useSidebarFilters();
  const query = useQuery<QueryInterestProfile, QueryInterestProfileVariables>(
    QUERY_INTEREST_PROFILE,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        interestId: props.interestID ?? "",
      },
    },
  );

  if (!props.interestID) return null;

  if (query.loading) {
    return null;
  }

  if (
    query.error ||
    !query.data?.interest?.interestList.property
  ) {
    return <ServerError desc={"There was a problem loading the page."} />;
  }

  const {
    interest: {
      id,
      householdMember0FirstName: first_name,
      householdMember0LastName: last_name,
      status: { name: status },
      interestList: { id: listID, name: listName, defaultSorting, property: { features } },
    },
  } = query.data;

  const documentsFeatureEnabled = isPropertyFeatureEnabled(features, Feature.DOCUMENT_UPLOAD);

  const slideoutEnabled = isPropertyFeatureEnabled(features, Feature.INTEREST_PROFILE_SLIDEOUT);
  if (slideoutEnabled) {
    navigate(`/dash/manager/lists/${listID}/p/1/interests/${id}/activity`);
    return null;
  }

  const links = [
    { name: "Messages & activity", to: "activity", icon: "fa-clock-o" },
    { name: "Application details", to: "update", icon: "fa-pencil" },
    {
      name: "Documents",
      to: "documents",
      icon: "fa-file",
      disabled: !documentsFeatureEnabled,
    },
  ];

  return (
    <div className={css("container", styles.profile)}>
      <div className={css("card", styles.header)}>
        <div className={styles.labels}>
          <Link className={styles.label}
            to={`/dash/manager/lists/${listID}/p/${(pageState.page ?? 0) + 1}`}>
            {listName}
          </Link>
          <span className={css(styles.divider, "fa", "fa-chevron-right")} />
          <span className={styles.label}>{[first_name, last_name].filter(identity).join(" ")}</span>
        </div>
        <div className={styles.controls}>
          <div className={styles.download}>
            <Download interestID={id} />
          </div>
          <div className={styles.actions}>
            <Actions currentListId={listID} currentInterestId={id} currentStatus={status} />
          </div>
        </div>
      </div>
      <br/>
      <Grid container spacing={2}>
        <Grid item sm={3} xs={12}>
          <Details query={query} />
        </Grid>
        <Grid item sm={9} xs={12}>
          <div className={styles.navigation}>
            <ul className={css("nav", "nav-tabs", styles.sections)}>
              {links
                .filter((link) => !link.disabled)
                .map((link) => (
                  <Match<{ page: string }> key={link.name} path=":page">
                    {(matchProps) => (
                      <li
                        role="presentation"
                        className={css({
                          active: matchProps.match && link.to === matchProps.match.page,
                        })}
                      >
                        <Link to={link.to}>
                          <span className={`fa ${link.icon}`} style={{ paddingRight: 8 }} />
                          {link.name}
                        </Link>
                      </li>
                    )}
                  </Match>
                ))}
            </ul>
            <div>
              <Navigation interestID={id} listID={listID} defaultSorting={defaultSorting || "POSITION_NUMBER"} />
            </div>
          </div>
          <Router primary={false}>
            <Activity path="/" viewer={props.viewer} interestID={id} interestListID={listID} />
            <Activity
              path="/activity"
              viewer={props.viewer}
              interestID={id}
              interestListID={listID}
            />
            <InterestUpdatePage
              path="/update"
              interestID={id}
              interestListID={listID}
              onCancel={() => navigate(`/dash/manager/interest/${props.interestID}/activity`)}
              onSubmit={() =>
                new Promise((resolve) => {
                  query.refetch().then(() => resolve());
                })
              }
            />
            <Documents path="/documents" interestID={id} enabled={documentsFeatureEnabled} />
          </Router>
        </Grid>
      </Grid>
    </div>
  );
};
